/* eslint-disable spaced-comment */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { FormGroup, ModalFooter, Label } from "reactstrap";
import useFetcher from "../../../hooks/useFetcher";
import { fetchNovedadPiAsignable } from "../../../services/novedadesServices";
import { StyledSelect } from "../../../components/StyledSelect";
import { handleErrors } from "../../../services/handleErrors";
import SpinnerButton from "../../../components/Button";
import { updateAreaCentralizado, postSubclientArea } from "../../../services/areaServices";
import ConfirmModal from "./ConfirmModal";
import compareDataChanges from "../../../utils/compareDataChanges";
import ConfirmCentralizado from "./ConfirmCentralizado";
import { useAuth } from "../../../context/AuthContext";
import modulos from "../../../mocks/modulos";
import { areaUtil } from "../../../utils/areaUtil";

function Sucursal({ centralizado, ClientId, subClient, subclientAreaData, handleChangesToSave }) {
  const { currentUser } = useAuth();
  const isAdmin = currentUser && currentUser.haveAdminAccess(modulos.CLIENTES);
  const isAudit = currentUser && currentUser.haveAuditAccess(modulos.CLIENTES);
  const isOwner = useMemo(() => areaUtil(subclientAreaData).isOwner(currentUser.user), [subclientAreaData, currentUser.user]);
  const readonly = (!isAdmin && !isOwner) || isAudit;

  const [confirmarModal, setConfirmarModal] = useState(false);
  const [centralizadoModal, setCentralizadoModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [changes, setChanges] = useState({ responsableId: false, liderId: false, observaciones: false });

  const formik = useFormik({
    initialValues: {
      responsableId: (subclientAreaData && subclientAreaData.responsableId) || null,
      liderId: (subclientAreaData && subclientAreaData.liderId) || null,
      observaciones: (subclientAreaData && subclientAreaData.observaciones) || "",
      convenio: (subClient && subClient.convenio) || "",
      alicuota: (subClient && subClient.alicuota) || "",
      jurisdiccion: (subClient && subClient.jurisdiccion) || "",
      modalidadDeTrabajo: (subClient && subClient.modalidadDeTrabajo) || "",
      condicionDePago: (subClient && subClient.condicionDePago) || "",
      cantidadDePass: (subClient && subClient.cantidadDePass) || "",
    },
    onSubmit: async (area) => {
      setIsLoading(true);
      const filteredInfo = compareDataChanges(area, subclientAreaData);
      try {
        if (subclientAreaData) {
          await updateAreaCentralizado(subclientAreaData.id, ClientId, filteredInfo);
        } else {
          await postSubclientArea(filteredInfo);
        }
        toast.success("Área actualizada");
        setConfirmarModal(false);
        setCentralizadoModal(false);
        handleChangesToSave(false);
        setChanges({ responsableId: false, liderId: false, observaciones: false });
        setIsLoading(false);
      } catch (error) {
        handleErrors(error, toast.error);
        setIsLoading(false);
      }
    },
    validationSchema: Yup.object().shape({
      responsableId: Yup.number().nullable(),
      liderId: Yup.number().nullable(),
      observaciones: Yup.string().nullable().notRequired(),
    }),
  });

  const [pisAsignable, isLoadingAsignable] = useFetcher(fetchNovedadPiAsignable);

  const piOptions = pisAsignable
    ? pisAsignable.map((assigned) => {
        return { label: assigned.nombreApellido, value: assigned.itemId };
      })
    : [];

  const responsableValue = piOptions.find((option) => {
    return option.value === formik.values.responsableId;
  });

  const liderValue = piOptions.find((option) => {
    return option.value === formik.values.liderId;
  });

  const handleBlur = (value) => {
    if (subclientAreaData) {
      if (formik.values[value] == null) {
        handleChangesToSave(true);
        setChanges((prevState) => ({ ...prevState, [value]: true }));
        return;
      }
      if (formik.values[value] !== subclientAreaData[value]) {
        handleChangesToSave(true);
        setChanges((prevState) => ({ ...prevState, [value]: true }));
        return;
      }
      if (formik.values[value] === subclientAreaData[value]) {
        handleChangesToSave(false);
        setChanges((prevState) => ({ ...prevState, [value]: false }));
        return;
      }
    }
    if (formik.values[value] != null || formik.values[value] !== "") {
      handleChangesToSave(true);
      setChanges((prevState) => ({ ...prevState, [value]: true }));
    } else {
      handleChangesToSave(false);
      setChanges((prevState) => ({ ...prevState, [value]: false }));
    }
  };

  return (
    <>
      {confirmarModal && <ConfirmModal isLoading={isLoading} toggle={() => setConfirmarModal(!confirmarModal)} handleSubmit={formik.handleSubmit} />}
      {centralizadoModal && (
        <ConfirmCentralizado
          ClientId={ClientId}
          area="sucursal"
          areaColor="#00a9e0"
          isLoading={isLoading}
          toggle={() => setCentralizadoModal(!centralizadoModal)}
          handleSubmit={formik.handleSubmit}
        />
      )}
      <FormGroup className="d-flex justify-content-between">
        <Label htmlFor="responsableId" className="col-form-label pt-0 w-100">
          Responsable
          <StyledSelect
            id="responsableId"
            name="responsableId"
            className="w-100"
            formik={formik}
            required
            value={responsableValue}
            placeholder="Seleccione responsable"
            isClearable
            disabled={formik.isSubmitting || isLoadingAsignable}
            isLoading={isLoadingAsignable}
            options={piOptions}
            onBlur={() => handleBlur("responsableId")}
            isDisabled={readonly}
          />
          {changes.responsableId ? <div className="input-feedback">Cambios por guardar</div> : null}
        </Label>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between">
        <Label htmlFor="liderId" className="col-form-label pt-0 w-100">
          Líder
          <StyledSelect
            id="liderId"
            name="liderId"
            className="w-100"
            formik={formik}
            required
            value={liderValue}
            placeholder="Seleccione lider"
            isClearable
            isLoading={isLoadingAsignable}
            disabled={formik.isSubmitting || isLoadingAsignable}
            options={piOptions}
            onBlur={() => handleBlur("liderId")}
            isDisabled={readonly}
          />
          {changes.liderId ? <div className="input-feedback">Cambios por guardar</div> : null}
        </Label>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between" style={{ margin: 0 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <Label htmlFor="convenio" className="col-form-label pt-0 w-100">
            Convenio
          </Label>
          <textarea name="convenio" cols="63" rows="2" value={formik.values.convenio} onChange={formik.handleChange} onBlur={() => handleBlur("convenio")} disabled />
          {changes.convenio ? <div className="input-feedback">Cambios por guardar</div> : null}
        </div>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between" style={{ margin: 0 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <Label htmlFor="alicuota" className="col-form-label pt-0 w-100">
            Alícuota
          </Label>
          <textarea name="alicuota" cols="63" rows="2" value={formik.values.alicuota} onChange={formik.handleChange} onBlur={() => handleBlur("alicuota")} disabled />
          {changes.alicuota ? <div className="input-feedback">Cambios por guardar</div> : null}
        </div>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between" style={{ margin: 0 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <Label htmlFor="jurisdiccion" className="col-form-label pt-0 w-100">
            Jurisdicción
          </Label>
          <textarea
            name="jurisdiccion"
            cols="63"
            rows="2"
            value={formik.values.jurisdiccion}
            onChange={formik.handleChange}
            onBlur={() => handleBlur("jurisdiccion")}
            disabled
          />
          {changes.jurisdiccion ? <div className="input-feedback">Cambios por guardar</div> : null}
        </div>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between" style={{ margin: 0 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <Label htmlFor="modalidadDeTrabajo" className="col-form-label pt-0 w-100">
            Modalidad de Trabajo
          </Label>
          <textarea
            name="modalidadDeTrabajo"
            cols="63"
            rows="2"
            value={formik.values.modalidadDeTrabajo}
            onChange={formik.handleChange}
            onBlur={() => handleBlur("modalidadDeTrabajo")}
            disabled
          />
          {changes.modalidadDeTrabajo ? <div className="input-feedback">Cambios por guardar</div> : null}
        </div>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between" style={{ margin: 0 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <Label htmlFor="condicionDePago" className="col-form-label pt-0 w-100">
            Condición de Pago
          </Label>
          <textarea
            name="condicionDePago"
            cols="63"
            rows="2"
            value={formik.values.condicionDePago}
            onChange={formik.handleChange}
            onBlur={() => handleBlur("condicionDePago")}
            disabled
          />
          {changes.condicionDePago ? <div className="input-feedback">Cambios por guardar</div> : null}
        </div>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between" style={{ margin: 0 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <Label htmlFor="cantidadDePass" className="col-form-label pt-0 w-100">
            Cantidad de Pass
          </Label>
          <textarea
            name="cantidadDePass"
            cols="63"
            rows="2"
            value={formik.values.cantidadDePass}
            onChange={formik.handleChange}
            onBlur={() => handleBlur("cantidadDePass")}
            disabled
          />
          {changes.cantidadDePass ? <div className="input-feedback">Cambios por guardar</div> : null}
        </div>
      </FormGroup>
      <FormGroup className="d-flex justify-content-between" style={{ margin: 0 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <Label htmlFor="observaciones" className="col-form-label pt-0 w-100">
            Observaciones
          </Label>
          <textarea
            name="observaciones"
            cols="63"
            rows="3"
            value={formik.values.observaciones}
            onChange={formik.handleChange}
            onBlur={() => handleBlur("observaciones")}
            disabled={readonly}
          />
          {changes.observaciones ? <div className="input-feedback">Cambios por guardar</div> : null}
        </div>
      </FormGroup>
      <ModalFooter>
        <FormGroup className="mb-0 px-3">
          {!readonly && (
            <SpinnerButton
              className="btn btn-primary btn-block"
              onClick={() => (centralizado ? setCentralizadoModal(!centralizadoModal) : setConfirmarModal(!confirmarModal))}
            >
              Guardar Cambios
            </SpinnerButton>
          )}
        </FormGroup>
      </ModalFooter>
    </>
  );
}

export default Sucursal;
